
import { Component, ChangeDetectionStrategy, signal, input, output, inject, computed } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService } from '../../services/auth.service';
import { AppUser } from '../../models/index';

interface NavItem {
  nameKey: string;
  icon: string;
  path: string;
  feature?: string;
  roles?: AppUser['role'][];
  businessTypes?: ('CLINIC' | 'DISPENSARY')[]; 
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, TranslatePipe]
})
export class SidebarComponent {
  private authService = inject(AuthService);

  isOpen = input<boolean>(false);
  closeMenu = output<void>();
  
  // Computed to check if user is platform admin for special menu item
  isPlatformAdmin = computed(() => this.authService.currentUser()?.role === 'platform_admin');

  private allNavItems: NavItem[] = [
    { 
      nameKey: 'backOffice.sidebar.dashboard', 
      icon: 'fa-solid fa-chart-pie', 
      path: '/admin/dashboard',
      roles: ['admin', 'owner', 'doctor', 'platform_admin'] 
    },
    { 
      nameKey: 'backOffice.sidebar.kiosk', 
      icon: 'fa-solid fa-desktop', 
      path: '/pos',
      roles: ['cashier', 'staff', 'admin', 'owner', 'platform_admin']
    },
    { 
      nameKey: 'backOffice.sidebar.customers', 
      icon: 'fa-solid fa-id-card-clip', 
      path: '/admin/customers' 
    },
    { 
      nameKey: 'backOffice.sidebar.telemedicine', 
      icon: 'fa-solid fa-user-doctor', 
      path: '/admin/tele-medicine', 
      feature: 'telemedicine',
      roles: ['doctor', 'admin', 'owner', 'platform_admin'],
      businessTypes: ['CLINIC'] // UPDATED: Only CLINIC type should see Doctor Station
    },
    { 
      nameKey: 'backOffice.sidebar.pharmacy', 
      icon: 'fa-solid fa-file-contract', 
      path: '/admin/pharmacy', 
      roles: ['cashier', 'admin', 'owner', 'staff', 'accountant', 'platform_admin']
    },
    {
      nameKey: 'backOffice.sidebar.compliance', 
      icon: 'fa-solid fa-file-shield',
      path: '/admin/compliance',
      roles: ['admin', 'owner', 'platform_admin']
    },
    { 
      nameKey: 'backOffice.sidebar.reports', 
      icon: 'fa-solid fa-chart-line', 
      path: '/admin/reports', 
      feature: 'reporting',
      roles: ['admin', 'owner', 'accountant', 'platform_admin'] 
    },
    { 
      nameKey: 'backOffice.sidebar.partnerNetwork', 
      icon: 'fa-solid fa-handshake', 
      path: '/admin/partner-network', 
      feature: 'partner_network', 
      roles: ['admin', 'owner', 'platform_admin'] 
    },
    { 
      nameKey: 'backOffice.sidebar.employees', 
      icon: 'fa-solid fa-users-gear', 
      path: '/admin/employees',
      roles: ['admin', 'owner', 'platform_admin']
    },
    { 
      nameKey: 'backOffice.sidebar.security', 
      icon: 'fa-solid fa-user-shield', 
      path: '/admin/audit-logs',
      roles: ['admin', 'owner', 'platform_admin']
    },
    { 
      nameKey: 'backOffice.sidebar.billing', 
      icon: 'fa-solid fa-credit-card', 
      path: '/admin/billing',
      roles: ['owner', 'admin', 'platform_admin']
    },
    { 
      nameKey: 'backOffice.sidebar.settings', 
      icon: 'fa-solid fa-sliders', 
      path: '/admin/settings',
      roles: ['admin', 'owner', 'platform_admin'],
      businessTypes: ['CLINIC'] // UPDATED: Hide Settings for Dispensary as requested
    },
    { 
      nameKey: 'backOffice.sidebar.profile', 
      icon: 'fa-solid fa-user-circle', 
      path: '/admin/profile' 
    },
  ];

  navItems = computed(() => {
    const userRole = this.authService.currentUser()?.role;

    // Platform Admin sees everything relevant to store management + special link below
    if (userRole === 'platform_admin') {
      return this.allNavItems;
    }
    
    const currentStore = this.authService.currentStore();
    const businessType = currentStore?.businessType || 'CLINIC'; 
    
    return this.allNavItems.filter(item => {
      const hasFeature = item.feature ? this.authService.hasFeature(item.feature)() : true;
      if (!hasFeature) return false;

      if (item.roles && userRole) {
        if (!item.roles.includes(userRole)) return false;
      }

      if (item.businessTypes) {
        if (!item.businessTypes.includes(businessType)) return false;
      }

      return true;
    });
  });

  onLinkClick(): void {
    this.closeMenu.emit();
  }
}
